import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Ruler, Phone, Check } from 'lucide-react';

const CALF_STEPS = [
  {
    photo: '/images/sigvaris/guide/measure-1.jpg',
    title: 'Ankle circumference',
    text: 'Measure at the narrowest part of the ankle, just above the ankle bone. This should always be your first measurement.',
  },
  {
    photo: '/images/sigvaris/guide/measure-2.jpg',
    title: 'Calf circumference',
    text: 'Measure around the fullest part of the calf.',
  },
  {
    photo: '/images/sigvaris/guide/measure-3.jpg',
    title: 'Calf length',
    text: 'Measure on the outside of the leg, from the floor up to the bend of the knee.',
  },
];

const THIGH_STEPS = [
  {
    photo: '/images/sigvaris/guide/measure-4.jpg',
    title: 'Thigh circumference',
    text: 'Measure the thigh at its widest circumference.',
  },
  {
    photo: '/images/sigvaris/guide/measure-5.jpg',
    title: 'Leg length',
    text: 'Measure from the floor up to the gluteal fold.',
  },
  {
    photo: '/images/sigvaris/guide/measure-6.jpg',
    title: 'Hip circumference',
    text: 'For pantyhose: measure around the widest part of the hips.',
  },
];

const TIPS = [
  'Measure as early in the morning as possible, when swelling is minimal',
  'Measure against bare skin, not over clothing',
  'Keep the foot flat on the floor with the leg at a right angle to the foot',
  'Pull the tape snug, but not constricting',
  'Take length measurements from the ground up',
  'Note your shoe size — it helps confirm the best fit',
];

function StepCards({ steps, startNumber }: { steps: typeof CALF_STEPS; startNumber: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {steps.map((step, i) => (
        <figure key={step.title} className="m-0 bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
          <img src={step.photo} alt={`Step ${startNumber + i}: ${step.title}`} className="w-full aspect-[3/4] object-contain bg-white" />
          <figcaption className="px-3 py-2.5">
            <p className="text-xs font-bold text-ndms-navy mb-0.5">{step.title}</p>
            <p className="text-[11px] text-gray-600 leading-snug">{step.text}</p>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

export function MeasuringGuideModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    // Capture phase + stopPropagation: Escape closes only this guide, not the
    // product modal underneath (which listens for Escape on document too).
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        // Don't let the backdrop click bubble to the product/size-guide modals
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto scrollbar-thin shadow-2xl"
      >
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-[#7B68CC] flex items-center justify-center">
              <Ruler size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-ndms-navy text-base leading-tight">How to Measure</h3>
              <p className="text-xs text-gray-500 leading-tight">From the official Sigvaris measuring guide</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-6">
          <div className="bg-[#f5f3ff] border border-[#7B68CC]/20 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-700 leading-snug">
              <span className="font-bold text-ndms-navy">Measure in the morning.</span> Legs swell during the day —
              measurements taken early, against bare skin, give the most accurate fit.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-bold text-ndms-navy mb-1">Calf (knee-high) styles — 3 steps</h4>
            <p className="text-xs text-gray-500 mb-3">Always measure in this order.</p>
            <StepCards steps={CALF_STEPS} startNumber={1} />
          </div>

          <div>
            <h4 className="text-sm font-bold text-ndms-navy mb-1">Thigh-high &amp; pantyhose styles — 3 more steps</h4>
            <p className="text-xs text-gray-500 mb-3">Start with steps 1 and 2 above, then continue:</p>
            <StepCards steps={THIGH_STEPS} startNumber={4} />
          </div>

          <div>
            <h4 className="text-sm font-bold text-ndms-navy mb-2">Good-measurement checklist</h4>
            <ul className="space-y-1.5">
              {TIPS.map((tip) => (
                <li key={tip} className="flex items-start gap-2 text-sm text-gray-600">
                  <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-ndms-navy rounded-2xl px-4 py-4 flex items-start gap-3">
            <Phone size={16} className="text-white/80 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-white/90 leading-snug">
              <span className="font-bold text-white">Prefer to be measured by us?</span> We do free in-store
              measuring and fitting — call{' '}
              <a href="tel:+13069794115" className="underline font-semibold text-white">306-979-4115</a>.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
